import { useEffect, useState } from "react";
import { Download, Loader2, Pencil, Trash2 } from "lucide-react";
import { api, API, formatDate, formatApiErrorDetail } from "../lib/api";
import RichEditor from "./RichEditor";

export default function AdminNewsletter() {
  const [subs, setSubs] = useState({ items: [], total: 0 });
  const [subStatus, setSubStatus] = useState("");
  const [subQ, setSubQ] = useState("");
  const [editions, setEditions] = useState([]);
  const [articles, setArticles] = useState([]);
  const [emailActive, setEmailActive] = useState(true);
  const [draft, setDraft] = useState({ subject: "", body_html: "", article_id: "" });
  const [editingEditionId, setEditingEditionId] = useState("");
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");

  const load = () => {
    const qs = new URLSearchParams();
    if (subStatus) qs.set("status", subStatus);
    if (subQ) qs.set("q", subQ);
    api.get(`/admin/newsletter/subscribers?${qs}`).then((r) => setSubs(r.data)).catch(() => {});
    api.get("/admin/newsletter/editions").then((r) => setEditions(r.data.items)).catch(() => {});
    api.get("/admin/dashboard").then((r) => setEmailActive(!!r.data.email_active)).catch(() => {});
    api.get("/admin/articles?status=published&limit=50").then((r) => setArticles(r.data.items)).catch(() => {});
  };

  useEffect(() => {
    const t = setTimeout(load, subQ ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subStatus, subQ]);

  const createEdition = async () => {
    setError("");
    setMsg("");
    try {
      const payload = {
        subject: draft.subject,
        body_html: draft.body_html,
        article_id: draft.article_id || null,
      };
      if (editingEditionId) {
        await api.put(`/admin/newsletter/editions/${editingEditionId}`, payload);
      } else {
        await api.post("/admin/newsletter/editions", payload);
      }
      setDraft({ subject: "", body_html: "", article_id: "" });
      setEditingEditionId("");
      load();
      setMsg(editingEditionId ? "Edisi diperbarui." : "Edisi disimpan sebagai draf.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const editEdition = (edition) => {
    setEditingEditionId(edition.id);
    setDraft({ subject: edition.subject, body_html: edition.body_html || "", article_id: "" });
    setError("");
    setMsg("");
  };

  const deleteEdition = async (edition) => {
    if (!window.confirm(`Hapus draf edisi "${edition.subject}"?`)) return;
    try {
      await api.delete(`/admin/newsletter/editions/${edition.id}`);
      if (editingEditionId === edition.id) {
        setEditingEditionId("");
        setDraft({ subject: "", body_html: "", article_id: "" });
      }
      load();
      setMsg("Draf edisi dihapus.");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  const sendTest = async (eid) => {
    setBusy(eid + "-test");
    setError("");
    try {
      const { data } = await api.post(`/admin/newsletter/editions/${eid}/test`);
      setMsg(data.message);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const sendEdition = async (eid) => {
    if (!window.confirm("Kirim edisi ini ke seluruh subscriber terkonfirmasi? Tindakan ini hanya sekali.")) return;
    setBusy(eid + "-send");
    setError("");
    try {
      const { data } = await api.post(`/admin/newsletter/editions/${eid}/send`);
      setMsg(data.message);
      load();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy("");
    }
  };

  const delSub = async (email) => {
    if (!window.confirm(`Hapus subscriber ${email}?`)) return;
    setError("");
    try {
      await api.delete(`/admin/newsletter/subscribers/${encodeURIComponent(email)}`);
      load();
    } catch (err) {
      if (err.response?.status !== 401) setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    }
  };

  return (
    <div data-testid="admin-newsletter-page">
      <h1 className="font-serif text-3xl text-ink">Newsletter</h1>

      {!emailActive && (
        <div className="mt-4 bg-surface-muted border border-line rounded-lg p-4 text-sm text-sub" data-testid="newsletter-email-status">
          Layanan email belum aktif — langganan tetap tercatat sebagai <em>pending</em>, pengiriman dinonaktifkan sampai konfigurasi email tersedia.
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        <section>
          <h2 className="font-serif text-2xl text-ink">Subscriber</h2>
          <div className="flex gap-3 mt-4 flex-wrap">
            <select data-testid="subscriber-status-filter" value={subStatus} onChange={(e) => setSubStatus(e.target.value)} className="field-input !min-h-[40px] !w-auto">
              <option value="">Semua status</option>
              <option value="confirmed">Terkonfirmasi</option>
              <option value="pending">Menunggu</option>
              <option value="unsubscribed">Berhenti</option>
            </select>
            <input data-testid="subscriber-search" value={subQ} onChange={(e) => setSubQ(e.target.value)} placeholder="Cari email…" className="field-input !min-h-[40px] !w-auto flex-1 max-w-[220px]" />
            <a href={`${API}/admin/newsletter/subscribers/export.csv`} data-testid="subscriber-export-button" className="btn-ghost !min-h-[40px] !px-4 text-sm">
              <Download size={15} /> Ekspor CSV
            </a>
          </div>
          <div className="bg-surface border border-line rounded-xl mt-4 overflow-hidden">
            <ul className="divide-y divide-line max-h-[420px] overflow-y-auto">
              {subs.items.map((s) => (
                <li key={s.email} className="p-3.5 flex items-center gap-3" data-testid={`subscriber-row-${s.email}`}>
                  <div className="flex-1 min-w-0">
                    <p className="text-[15px] text-ink truncate">{s.email}</p>
                    <p className="text-[12px] text-faint">{s.name || "—"} · {formatDate(s.created_at)} · {s.consent_source}</p>
                  </div>
                  <span className={`text-[11px] px-2 py-0.5 rounded-full font-semibold shrink-0 ${s.status === "confirmed" ? "bg-moss/15 text-moss" : s.status === "pending" ? "bg-indigo/15 text-indigo" : "bg-surface-muted text-faint"}`}>
                    {s.status === "confirmed" ? "aktif" : s.status === "pending" ? "menunggu" : "berhenti"}
                  </span>
                  <button onClick={() => delSub(s.email)} aria-label={`Hapus ${s.email}`} data-testid={`subscriber-delete-${s.email}`} className="w-9 h-9 rounded-md border border-line text-sub hover:text-enji shrink-0"><Trash2 size={14} className="mx-auto" /></button>
                </li>
              ))}
              {subs.items.length === 0 && <li className="p-5 text-faint text-sm">Belum ada subscriber.</li>}
            </ul>
          </div>
          <p className="text-[13px] text-faint mt-3">{subs.total} subscriber sesuai filter. Hanya alamat terkonfirmasi yang menerima edisi.</p>
        </section>

        <section>
          <h2 className="font-serif text-2xl text-ink">Edisi Baru</h2>
          <div className="bg-surface border border-line rounded-xl p-5 mt-4 space-y-4" data-testid="edition-form">
            <input data-testid="edition-subject-input" value={draft.subject} onChange={(e) => setDraft({ ...draft, subject: e.target.value })} placeholder="Subjek edisi" className="field-input" />
            <select data-testid="edition-article-select" value={draft.article_id} onChange={(e) => setDraft({ ...draft, article_id: e.target.value })} className="field-input !min-h-[40px]">
              <option value="">Tanpa artikel (tulisan bebas)</option>
              {articles.map((a) => <option key={a.id} value={a.id}>{a.title}</option>)}
            </select>
            <RichEditor value={draft.body_html} onChange={(html) => setDraft({ ...draft, body_html: html })} placeholder="Isi edisi…" />
            <div className="flex gap-2 flex-wrap">
              <button onClick={createEdition} data-testid="edition-create-button" className="btn-primary !min-h-[40px]">{editingEditionId ? "Simpan Perubahan" : "Simpan Edisi"}</button>
              {editingEditionId && <button type="button" onClick={() => { setEditingEditionId(""); setDraft({ subject: "", body_html: "", article_id: "" }); }} className="btn-ghost !min-h-[40px]">Batal</button>}
            </div>
          </div>

          <h2 className="font-serif text-2xl text-ink mt-8">Daftar Edisi</h2>
          <div className="mt-4 space-y-3">
            {editions.map((e) => (
              <div key={e.id} className="bg-surface border border-line rounded-lg p-4" data-testid={`edition-row-${e.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-ink text-[15px] truncate">{e.subject}</p>
                    <p className="text-[12px] text-faint mt-0.5">{e.status} · dibuat {formatDate(e.created_at)}{e.sent_at ? ` · terkirim ${formatDate(e.sent_at)} ke ${e.recipient_count} penerima` : ""}</p>
                  </div>
                  <div className="flex flex-wrap gap-1.5 shrink-0 justify-end">
                    {e.status !== "sent" && e.status !== "partial" && (
                      <>
                        <button onClick={() => editEdition(e)} data-testid={`edition-edit-${e.id}`} title="Sunting" className="w-9 h-9 rounded-md border border-line text-sub hover:text-enji"><Pencil size={14} className="mx-auto" /></button>
                        <button onClick={() => deleteEdition(e)} data-testid={`edition-delete-${e.id}`} title="Hapus" className="w-9 h-9 rounded-md border border-line text-sub hover:text-enji"><Trash2 size={14} className="mx-auto" /></button>
                      </>
                    )}
                    <button onClick={() => sendTest(e.id)} data-testid={`edition-test-${e.id}`} disabled={busy === e.id + "-test"} className="px-3 h-9 rounded-md border border-line text-[13px] font-semibold text-sub hover:text-ink">
                      {busy === e.id + "-test" ? <Loader2 size={14} className="animate-spin" /> : "Kirim Uji"}
                    </button>
                    {e.status !== "sent" && (
                      <button onClick={() => sendEdition(e.id)} data-testid={`edition-send-${e.id}`} disabled={busy === e.id + "-send"} className="px-3 h-9 rounded-md bg-enji text-white text-[13px] font-semibold">
                        {busy === e.id + "-send" ? <Loader2 size={14} className="animate-spin" /> : "Kirim"}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {editions.length === 0 && <p className="text-faint text-sm">Belum ada edisi.</p>}
          </div>

          {msg && <p data-testid="newsletter-admin-message" className="text-sm text-moss mt-4">{msg}</p>}
          {error && <p role="alert" className="text-sm text-enji mt-4">{error}</p>}
          <p className="text-[13px] text-faint mt-6 leading-relaxed">
            Email uji dikirim ke alamat pemilik terverifikasi (ADMIN_EMAIL). Pengiriman edisi hanya sekali per edisi dan hanya ke subscriber terkonfirmasi (double opt-in).
          </p>
        </section>
      </div>
    </div>
  );
}
